import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft,
  Building2,
  Loader2,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  Calendar,
} from "lucide-react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    business_name: "",
    email: "",
    mobile: "",
    password: "",
    confirm_password: "",
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.business_name.trim()) {
      toast.error("Please enter your business name");
      return;
    }

    if (!formData.email.trim()) {
      toast.error("Please enter your email");
      return;
    }

    if (!formData.mobile.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/register`, {
        business_name: formData.business_name,
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
      });

      toast.success("Registration submitted! Please verify your email.");

      navigate(
        `/auth/verify-email?email=${encodeURIComponent(formData.email)}`,
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* TOP HEADER */}
      <div className="h-16 flex items-center justify-between px-6 lg:px-12 border-b bg-white">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Link>

        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Calendar className="w-5 h-5 text-white" />
          </div>

          <span className="font-heading font-bold text-xl bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-600 bg-clip-text text-transparent">
            SlotBook
          </span>
        </Link>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex flex-1">
        {/* LEFT PANEL */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/90 to-primary" />

          <img
            src="https://images.unsplash.com/photo-1637777277337-f114350fb088?crop=entropy&cs=srgb&fm=jpg"
            alt="Modern salon"
            className="w-full h-full object-cover mix-blend-overlay"
          />

          <div className="absolute inset-0 flex flex-col justify-end p-12 text-white">
            <h2 className="font-heading text-4xl font-extrabold mb-4">
              Grow Your Business
            </h2>

            <p className="text-lg text-white/80 max-w-md mb-8">
              Accept bookings 24/7 and manage your salon effortlessly with
              SlotBook.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ Free to start
              </div>

              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ Setup in under 5 minutes
              </div>

              <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-sm">
                ✓ No credit card required
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-10 bg-slate-100">
          <div className="w-full max-w-xl bg-white border rounded-2xl shadow-xl p-8 lg:p-10">
            {/* TITLE */}
            <div className="mb-8 text-center">
              <h1 className="font-heading text-3xl font-extrabold mb-2">
                Create your account
              </h1>

              <p className="text-muted-foreground">
                Start accepting online bookings in minutes.
              </p>
            </div>

            {/* FORM */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* BUSINESS NAME */}
              <div className="space-y-2">
                <Label htmlFor="business_name">Business Name</Label>

                <div className="relative">
                  <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    id="business_name"
                    placeholder="Enter your business name"
                    value={formData.business_name}
                    onChange={(e) =>
                      updateField("business_name", e.target.value)
                    }
                    className="pl-10 h-12 text-base"
                    required
                  />
                </div>
              </div>

              {/* EMAIL */}
              <div className="space-y-2">
                <Label>Email Address</Label>

                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@business.com"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className="pl-10 h-12 text-base"
                    required
                  />
                </div>
              </div>

              {/* MOBILE */}
              <div className="space-y-2">
                <Label>Mobile Number</Label>

                <div className="relative">
                  <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="0400 000 000"
                    value={formData.mobile}
                    onChange={(e) => updateField("mobile", e.target.value)}
                    className="pl-10 h-12 text-base"
                    required
                  />
                </div>
              </div>

              {/* PASSWORD GRID */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* PASSWORD */}
                <div className="space-y-2">
                  <Label>Password</Label>

                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Min 6 characters"
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={(e) => updateField("password", e.target.value)}
                      className="pl-10 pr-10 h-12 text-base"
                      required
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    >
                      {showPassword ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* CONFIRM PASSWORD */}
                <div className="space-y-2">
                  <Label>Confirm Password</Label>

                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />

                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      value={formData.confirm_password}
                      onChange={(e) =>
                        updateField("confirm_password", e.target.value)
                      }
                      className="pl-10 h-12 text-base"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* SUBMIT */}
              <Button
                type="submit"
                className="w-full h-12 text-base"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  "Start My Free Account"
                )}
              </Button>

              {/* LOGIN */}
              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link
                  to="/auth/login"
                  className="text-primary font-medium hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
